// File: frontend/src/components/SEO/SEO.js
import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const SEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  robots = 'index, follow',
  twitterCard = 'summary_large_image',
  language,
  author,
  publishedTime,
  modifiedTime,
  noIndex = false,
  structuredData
}) => {
  const { t } = useLanguage();
  
  // Get configuration from environment variables
  const siteUrl = process.env.REACT_APP_APP_URL || 'https://example.com';
  const siteName = process.env.REACT_APP_SITE_NAME || 'Your Site';
  const twitterHandle = process.env.REACT_APP_TWITTER_HANDLE || '@yoursite';
  const defaultOgImage = `${siteUrl}/images/og-default.jpg`;
  
  // Use the language from context if not provided
  const pageLang = language || (t('language_code') || 'en-ca');
  
  // Build full URLs and titles
  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} - ${t('seo_default_title', 'Your trusted partner')}`;
  const fullCanonicalUrl = canonicalUrl ? 
    (canonicalUrl.startsWith('http') ? canonicalUrl : `${siteUrl}${canonicalUrl}`) : 
    undefined;
  const fullOgImage = ogImage ? 
    (ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`) : 
    defaultOgImage;
  
  // Handle robots meta based on environment and noIndex prop
  const robotsContent = noIndex || process.env.REACT_APP_APP_ENV === 'dev' || process.env.REACT_APP_APP_ENV === 'staging' 
    ? 'noindex, nofollow' 
    : robots;
    
  // Default structured data
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": siteName,
    "url": siteUrl,
    "description": description || t('seo_default_description', 'Your application description')
  };
  
  // Merge with custom structured data if provided
  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <>
      {/* HTML Lang Attribute */}
      <html lang={pageLang.split('-')[0]} />
      
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robotsContent} />
      {author && <meta name="author" content={author} />}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Canonical URL - only in production */}
      {fullCanonicalUrl && process.env.REACT_APP_APP_ENV === 'production' && (
        <link rel="canonical" href={fullCanonicalUrl} />
      )}
      
      {/* Open Graph Tags */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content={pageLang.replace('-', '_')} />
      {fullCanonicalUrl && <meta property="og:url" content={fullCanonicalUrl} />}
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || siteName} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={title || siteName} />
      
      {/* Favicon and App Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />
      
      {/* Additional Meta Tags for Sites */}
      <meta name="theme-color" content="#2348ED" />
      <meta name="msapplication-TileColor" content="#2348ED" />
      <meta name="application-name" content={siteName} />
      
      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Environment-specific meta tags */}
      {process.env.REACT_APP_APP_ENV !== 'production' && (
        <meta name="environment" content={process.env.REACT_APP_APP_ENV} />
      )}
      
      {/* Structured Data for better SEO */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    </>
  );
};

export default SEO;
